import type { TFunction } from 'i18next';
import type { CsvParseError } from './csvParse';

/**
 * Translate a structured `CsvParseError` at the component boundary.
 *
 * `csvParse.ts` is pure and cannot call `t()`, so it emits codes + context.
 * Without this bridge every parser error surfaced by the stocktake wizard would
 * be permanently English — which defeats the point on a surface built for
 * Chinese warehouse operators.
 *
 * Codes with no dedicated key fall back to the English `formatCsvParseError`
 * rendering the caller already has, so nothing is ever swallowed.
 */
export function translateCsvParseError(
  error: CsvParseError,
  t: TFunction,
  fallback: string,
): string {
  switch (error.code) {
    case 'missing_column':
      return t('bulkStocktake.err.missingColumn', { column: error.column });
    case 'duplicate_column':
      return t('bulkStocktake.err.duplicateColumn', {
        first: error.first,
        second: error.second,
        canonical: error.canonical,
      });
    case 'field_count':
      return t('bulkStocktake.err.fieldCount', {
        line: error.line,
        got: error.got,
        expected: error.expected,
      });
    case 'unterminated_quote':
      return t('bulkStocktake.err.unterminatedQuote');
    case 'rejected_header':
      // Already an operator-facing sentence chosen by the parser config
      // (e.g. the Add Stock "this is a count sheet" guard).
      return error.reason;
    default:
      return fallback;
  }
}
